import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import { getDocFormPendingReadyForReview, generateMagicLink, cancelFormRequest } from "../../../../services/templateV2Service";
import { IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, List, ListItem, ListItemText, ListItemSecondaryAction, Menu, MenuItem, ListItemIcon } from "@mui/material";
import VisibilityIcon from "@material-ui/icons/Visibility";
import LinkIcon from "@material-ui/icons/Link";
import CancelIcon from "@material-ui/icons/Cancel";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { MoreVert as MoreVertIcon, Schedule as ScheduleIcon, Autorenew as AutorenewIcon, CheckCircle as CheckCircleIcon } from "@material-ui/icons";
import TableSkeletonLoader from '../TableSkeletonLoader/TableSkeletonLoader';
import TablePagination from "@mui/material/TablePagination";
import moment from "moment-timezone";
import { BaseDataTable } from "../../../../components/common/BaseDataTable";
import { colors } from "../../../../components/theme";

const STATUS_CONFIG = {
  not_started: {
    label: "Not started",
    icon: ScheduleIcon,
    bg: "#F3F4F6",
    color: "#6B7280",
  },
  in_progress: {
    label: "In progress",
    icon: AutorenewIcon,
    bg: "#EFF6FF",
    color: colors.blue,
  },
  completed: {
    label: "Completed",
    icon: CheckCircleIcon,
    bg: "#ECFDF5",
    color: "#059669",
  },
};

const useStyles = makeStyles((theme) => ({
  container: {
    width: "100%",
    height: "100%",
    backgroundColor: "#fff",
    display: "inline-block",
    padding: "10px",
    position: "relative",
    overflowY: "hidden",
    borderRadius: "8px !important",
  },
  actionText: {
    fontSize: "14px",
    marginLeft: "4px",
    color: colors.blue,
  },
  statusPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 10px",
    borderRadius: 9999,
    fontSize: 12,
    fontWeight: 500,
  },
}));

export default function PendingView({ setLoading }) {
  const classes = useStyles();

  const [listDocuments, setListDocuments] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openPeopleDialog, setOpenPeopleDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const navigate = useNavigate();
  const [isLoadingData, setIsLoadingData] = useState(true);

	const fetchPendingList = async () => {
		try {
			setIsLoadingData(true);
			setLoading(true, "Loading sent forms...");
			const res = await getDocFormPendingReadyForReview();
			setListDocuments(res.data || []);
		} catch (error) {
			console.error("Error fetching sent forms:", error);
		} finally {
			setIsLoadingData(false);
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchPendingList();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };
  const paginatedRows = listDocuments.slice(
    currentPage * rowsPerPage,
    currentPage * rowsPerPage + rowsPerPage
  );

  const toComplete = (row) => {
    setLoading(true, "Loading...");
    const urlParams = new URLSearchParams({
      urlPdf: row.pdfUrl,
      ...(row.flowId && { flowId: row.flowId })
    });
    navigate(
      `/docFormViewer/${row.templateId}/${row.id}?${urlParams.toString()}`
    );
  };
	const viewFile = async (row) => {
		console.log('row', row);
		//open a new tab with the url
		window.open(row.pdfUrl, '_blank');
	};

  const handleCancelRequest = async (row) => {
    const flowId = row?.flowId;
    if (!flowId) {
      toast.error("Cannot cancel: form request has no flow ID.");
      return;
    }
    if ((row?.status || "").toLowerCase() === "completed") {
      toast.error("Cannot cancel a form request that is already completed.");
      return;
    }
    const isInProgress = (row?.status || "").toLowerCase() === "in_progress";
    const confirmOptions = isInProgress
      ? {
          title: "Cancel this form request?",
          html: "Recipients have already started filling this form. If you cancel, it will be removed for you and for all recipients.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#d33",
          cancelButtonColor: "#3085d6",
          confirmButtonText: "Yes, cancel request",
        }
      : {
          title: "Cancel this form request?",
          text: "It will be removed for you and for all recipients.",
          icon: "question",
          showCancelButton: true,
          confirmButtonColor: "#d33",
          cancelButtonColor: "#3085d6",
          confirmButtonText: "Yes, cancel request",
        };
    const result = await Swal.fire(confirmOptions);
    if (!result.isConfirmed) return;
    try {
      setLoading(true, "Cancelling form request...");
      const response = await cancelFormRequest(flowId);
      const message = response?.data?.message || "Form request cancelled.";
      toast.success(message);
      handleCloseMenu();
      await fetchPendingList();
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || "Failed to cancel form request.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleClickMenuItem = (menu, template) => {
    switch (menu) {
      case "Details":
        toComplete(template);
        break;
      case "Cancel":
        handleCancelRequest(template);
        break;
      default:
        break;
    }
  };

  const handleOpenPeopleDialog = (row) => {
    setSelectedRow(row);
    setOpenPeopleDialog(true);
    handleCloseMenu();
  };

  const handleClosePeopleDialog = () => {
    setOpenPeopleDialog(false);
    setSelectedRow(null);
  };

  const handleCopyMagicLink = async (person) => {
    if (!person.formId) {
        toast.error("Form ID not found for this person.");
        return;
    }

    if (person.completed === 1) {
        toast.info("This form has already been completed.");
        return;
    }

    try {
        setLoading(true, "Generating Magic Link...");
        const response = await generateMagicLink(person.formId);
        if (response.data && response.data.success) {
            navigator.clipboard.writeText(response.data.data.magic_link);
            toast.success(`Magic Link copied for ${person.name}!`);
        } else {
            toast.error("Failed to generate Magic Link.");
        }
    } catch (error) {
        console.error("Error generating magic link:", error);
        toast.error("Error generating Magic Link.");
    } finally {
        setLoading(false);
    }
  };

  const handleOpenMenu = (event, row) => {
    setAnchorEl(event.currentTarget);
    setMenuRow(row);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuRow(null);
  };


	const columns = [
		{
			field: "templateName",
			headerName: "Document Name",
			flex: 1.5,
			headerClassName: "MuiTableHead-root",
		},
		{
			field: "created_at",
			headerName: "Request Date",
			flex: 1,
			headerClassName: "MuiTableHead-root",
			valueFormatter: (params) =>
				params.value
					? moment(params.value).tz("America/New_York").format("MM/DD/YYYY hh:mm")
					: "N/A",
		},
		{
			field: "status",
			headerName: "Status",
			flex: 0.8,
			headerClassName: "MuiTableHead-root",
			align: "center",
			headerAlign: "center",
			renderCell: (params) => {
				const key = (params.value || "").toLowerCase().replace(/\s+/g, "_");
				const config = STATUS_CONFIG[key] || {
					label: params.value || "—",
					icon: ScheduleIcon,
					bg: "#F3F4F6",
					color: "#6B7280",
				};
				const Icon = config.icon;
				return (
					<span
						className={classes.statusPill}
						style={{ backgroundColor: config.bg, color: config.color }}
					>
						<Icon style={{ fontSize: 16 }} />
						{config.label}
					</span>
				);
			},
		},
		{
			field: "actions",
			headerName: "Actions",
			flex: 0.8,
			headerClassName: "MuiTableHead-root",
			align: "center",
			headerAlign: "center",
			renderCell: (params) => (
				<div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
					<IconButton
						aria-label="more"
						aria-controls="long-menu"
						aria-haspopup="true"
						onClick={(e) => handleOpenMenu(e, params.row)}
					>
						<MoreVertIcon />
					</IconButton>
				</div>
			),
		},
	];

	return (
		<div className={classes.container}>
			{isLoadingData ? (
				<TableSkeletonLoader rows={8} columns={4} />
			) : (
				<>
					<BaseDataTable
						data={paginatedRows.map((row, idx) => ({
							...row,
							id: row.id || row.formId || `sent-${currentPage * rowsPerPage + idx}`,
						}))}
						columns={columns}
						loading={isLoadingData}
						pageSize={rowsPerPage}
						rowsPerPageOptions={[10, 25, 50, 100]}
						disableSelectionOnClick
						hideFooter
						autoHeight={paginatedRows.length <= 10}
						option={({ action, id, row }) => handleClickMenuItem(action, row)}
					/>
					<div style={{ padding: "6px 8px", color: "#6B7280", fontSize: 12 }}>
						Showing {listDocuments.length === 0 ? 0 : currentPage * rowsPerPage + 1}–
						{Math.min((currentPage + 1) * rowsPerPage, listDocuments.length)} of {listDocuments.length} forms
					</div>
					{listDocuments.length > 0 && (
						<TablePagination
							component="div"
							count={listDocuments.length}
							page={currentPage}
							onPageChange={handleChangePage}
							rowsPerPage={rowsPerPage}
							onRowsPerPageChange={handleChangeRowsPerPage}
							rowsPerPageOptions={[10, 25, 50, 100]}
							showFirstButton
							showLastButton
						/>
					)}
				</>
			)}
            <Menu
                id="long-menu"
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleCloseMenu}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{
                    style: {
                        maxHeight: 48 * 4.5,
                        width: "20ch",
                    },
                }}
            >
                {menuRow && menuRow.status === "completed" ? (
                    <MenuItem onClick={() => { viewFile(menuRow); handleCloseMenu(); }}>
                        <ListItemIcon>
                            <VisibilityIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="View" />
                    </MenuItem>
                ) : (
                    menuRow && (
                        <div>
                            <MenuItem onClick={() => { toComplete(menuRow); handleCloseMenu(); }}>
                                <ListItemIcon>
                                    <VisibilityIcon fontSize="small" color="primary" />
                                </ListItemIcon>
                                <ListItemText primary="To Complete" />
                            </MenuItem>
                            {menuRow.peopleInvolved && menuRow.peopleInvolved.length > 0 && (
                                <MenuItem onClick={() => handleOpenPeopleDialog(menuRow)}>
                                    <ListItemIcon>
                                        <LinkIcon fontSize="small" color="primary" />
                                    </ListItemIcon>
                                    <ListItemText primary="Links" />
                                </MenuItem>
                            )}
                            <MenuItem
                                onClick={() => { handleCancelRequest(menuRow); handleCloseMenu(); }}
                                style={{ color: "#A8071A" }}
                            >
                                <ListItemIcon>
                                    <CancelIcon fontSize="small" style={{ color: "#A8071A" }} />
                                </ListItemIcon>
                                <ListItemText primary="Cancel request" primaryTypographyProps={{ style: { color: "#A8071A" } }} />
                            </MenuItem>
                        </div>
                    )
                )}
            </Menu>

            <Dialog open={openPeopleDialog} onClose={handleClosePeopleDialog} fullWidth maxWidth="sm">
                <DialogTitle>People Involved</DialogTitle>
                <DialogContent dividers>
                    <List>
                        {selectedRow && selectedRow.peopleInvolved && selectedRow.peopleInvolved.map((person, index) => (
                            <ListItem key={index}>
                                <ListItemText
                                    primary={person.name}
                                    secondary={person.email}
                                />
                                <ListItemSecondaryAction>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        size="small"
                                        startIcon={<LinkIcon />}
                                        onClick={() => handleCopyMagicLink(person)}
                                        disabled={person.completed === 1}
                                    >
                                        Copy Link
                                    </Button>
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClosePeopleDialog} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
		</div>
	);
} 
